import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";

import { Organization } from "../utils/models";
import { AppDispatch, RootState } from "./configureStore";
import { getCurrentUser } from "../services/authService";
import { USER_TYPES } from "../utils/constants";

import * as service from "../services/organizationService";
import * as utils from "../utils/utilities";

export interface OrganizationState {
  loading: boolean;
  error: boolean;
  lastUpdated: null | number;
  organizations: Organization[]
}

const initialState: OrganizationState = {
  loading: false,
  error: false,
  lastUpdated: null,
  organizations: [],
};

const slice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    organizationsRequested: (state) => {
      state.loading = true;
    },
    organizationsRequestSuccess: (state, action: PayloadAction<Organization[]>) => {
      state.organizations = action.payload;
      state.loading = false;
      state.error = false;
    },
    organizationsRequestFailed: (state) => {
      state.loading = false;
      state.error = true;
    },
    updateSyncTime: (state) => {
      state.lastUpdated = Date.now();
    },
  },
});

export const loadOrganizations = () => async (dispatch: AppDispatch) => {
  const user = getCurrentUser();
  if (user && user.userType !== USER_TYPES.INTERNAL) return

  try {
    dispatch(slice.actions.organizationsRequested());

    const result = await service.getAll();

    dispatch(slice.actions.organizationsRequestSuccess(result.data));
    dispatch(slice.actions.updateSyncTime());
  } catch (ex) {
    const cachedOrganizations = utils.getCachedDataFromException(ex);
    if (!cachedOrganizations)
      return dispatch(slice.actions.organizationsRequestFailed());

    dispatch(slice.actions.organizationsRequestSuccess(cachedOrganizations));
  }
};

export const getOrganizations = createSelector(
  (state: RootState) => state,
  (state) => state.organizations
);

export default slice.reducer;
